import { useState, useEffect } from "react";

// simple Office state interface
interface OfficeState {
    is_loading: boolean;
    is_ready: boolean;
    error: string | null;
    context: any;
}

// simple Office environment detection
export const isOfficeEnvironment = (): boolean => {
    if (typeof window === "undefined") return false;

    return (
        window.location.search.includes("office=true") ||
        window.location.hostname.includes("office") ||
        document.referrer.includes("outlook") ||
        document.referrer.includes("office") ||
        window !== window.top // in iframe
    );
};

// dynamic Office.js script loading
const loadOfficeScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
        // check if Office.js is already loaded
        if (typeof (globalThis as any).Office !== "undefined") {
            resolve();
            return;
        }

        // check if we're in an environment where Office.js should already be loaded
        // (like Outlook web where it's already included)
        if (document.querySelector('script[src*="office.js"]')) {
            // wait a bit for existing script to load
            const checkOffice = () => {
                if (typeof (globalThis as any).Office !== "undefined") {
                    resolve();
                } else {
                    setTimeout(checkOffice, 100);
                }
            };
            setTimeout(checkOffice, 100);
            return;
        }

        const script = document.createElement("script");
        script.src = "https://appsforoffice.microsoft.com/lib/1/hosted/office.js";
        script.async = true;

        script.onload = () => {
            resolve();
        };
        script.onerror = () => reject(new Error("Failed to load Office.js"));

        document.head.appendChild(script);
    });
};

// office initialization
const initializeOffice = (): Promise<any> => {
    return new Promise((resolve, reject) => {
        const Office = (globalThis as any).Office;

        if (!Office) {
            reject(new Error("Office.js not loaded"));
            return;
        }

        // check if Office is already initialized
        if (Office.context && Office.context.mailbox) {
            resolve({
                reason: "AlreadyInitialized",
                host: Office.context.host || "Unknown",
                platform: Office.context.platform || "Unknown"
            });
            return;
        }

        // use modern Office.onReady if available
        if (typeof Office.onReady === "function") {
            Office.onReady((info: any) => {
                resolve(info);
            });
        } else {
            // fallback to Office.initialize
            const originalInitialize = Office.initialize;
            Office.initialize = (reason: any) => {
                if (originalInitialize) originalInitialize(reason);
                resolve({ reason });
            };
        }

        // add timeout to avoid hanging forever
        setTimeout(() => {
            if (Office.context && Office.context.mailbox) {
                resolve({
                    reason: "TimeoutCheck",
                    host: Office.context.host || "Unknown",
                    platform: Office.context.platform || "Unknown"
                });
            } else {
                reject(new Error("Office initialization timeout - Office.onReady/initialize never called"));
            }
        }, 10000);
    });
};

// main hook
export const useOffice = () => {
    const [state, setState] = useState<OfficeState>({
        is_loading: false,
        is_ready: false,
        error: null,
        context: null
    });

    useEffect(() => {
        // only initialize if we're in Office environment
        if (!isOfficeEnvironment()) {
            return;
        }

        setState(prev => ({ ...prev, is_loading: true }));

        const initialize = async () => {
            try {
                // load Office.js script
                await loadOfficeScript();

                // initialize Office
                const context = await initializeOffice();

                setState({
                    is_loading: false,
                    is_ready: true,
                    error: null,
                    context
                });
            } catch (error: any) {
                setState({
                    is_loading: false,
                    is_ready: false,
                    error: error.message,
                    context: null
                });
            }
        };

        initialize();
    }, []);

    // office utilities (only work when ready)
    const getCurrentEmail = () => {
        if (!state.is_ready) return null;

        try {
            const Office = (globalThis as any).Office;
            return Office.context?.mailbox?.item || null;
        } catch {
            return null;
        }
    };

    const getOfficeTheme = () => {
        if (!state.is_ready) return { is_dark_theme: false };

        try {
            const Office = (globalThis as any).Office;
            const theme = Office.context?.officeTheme;
            return {
                is_dark_theme: theme?.bodyBackgroundColor === "#000000" ||
                    theme?.bodyForegroundColor === "#ffffff"
            };
        } catch {
            return { is_dark_theme: false };
        }
    };

    return {
        ...state,
        is_office_environment: isOfficeEnvironment(),
        getCurrentEmail,
        getOfficeTheme
    };
};